import React, { useState, useEffect, useRef, useCallback } from 'react';
import malabasSiteImg from '../images/malabas-site.png';
import malabasDonate  from '../images/malabas-donate.png';
import malabasSell    from '../images/malabas-sell.png';
import silsilaScreen1 from '../images/silsila-screen1.png';
import silsilaScreen2 from '../images/silsila-screen2.png';
import uscMapImg       from '../images/USCMap.png';
import studyscapeImg   from '../images/studyscape.png';

const KF = `
@keyframes pp-kb     { 0%{transform:scale(1) translate(0,0)} 40%{transform:scale(1.06) translate(-1.2%,0.8%)} 80%{transform:scale(1.07) translate(0.8%,-0.6%)} 100%{transform:scale(1) translate(0,0)} }
@keyframes pp-fade   { from{opacity:0} to{opacity:1} }
@keyframes pp-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes phoneIn   { from{opacity:0;transform:translateY(28px) scale(0.94)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes fadeUp    { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
@keyframes progRun   { from{width:0%} to{width:100%} }
`;

/* ── reusable browser chrome ── */
function BrowserFrame({ url, bg, children, animation = 'pp-fade 0.5s ease 0.2s both' }) {
  return (
    <div style={{
      width: '100%', height: '100%', minHeight: 280,
      background: bg, borderRadius: 12,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', padding: '16px 14px',
    }}>
      <style>{KF}</style>
      <div style={{
        width: '100%', borderRadius: 10, overflow: 'hidden',
        boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
        border: '1.5px solid rgba(0,0,0,0.12)',
        animation,
      }}>
        <div style={{
          height: 28, background: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex', alignItems: 'center', padding: '0 10px', gap: 5,
        }}>
          {['#e06c6c','#e8c06c','#6cbf6c'].map((c, i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
          ))}
          <div style={{
            flex: 1, marginLeft: 8, height: 15, borderRadius: 8, background: '#eaeaea',
            display: 'flex', alignItems: 'center', padding: '0 8px',
          }}>
            <span style={{ fontSize: 8, color: '#777', fontFamily: 'monospace' }}>{url}</span>
          </div>
        </div>
        <div style={{ overflow: 'hidden', lineHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Malabas ── slide between screens ── */
const MALABAS_SLIDES = [
  { img: malabasSiteImg, url: 'malabas.co/listings' },
  { img: malabasDonate,  url: 'malabas.co/donate'   },
  { img: malabasSell,    url: 'malabas.co/sell'      },
];
const MALABAS_DURATION = 3200;

export function MalabasPreview() {
  const [cur, setCur]         = useState(0);
  const [exiting, setExiting] = useState(null);
  const exitTimer  = useRef(null);
  const slideTimer = useRef(null);
  const curRef     = useRef(0);

  const goTo = useCallback((n) => {
    const prev = curRef.current;
    setExiting(prev);
    setCur(n);
    curRef.current = n;
    clearTimeout(exitTimer.current);
    exitTimer.current = setTimeout(() => setExiting(null), 450);
  }, []);

  useEffect(() => {
    slideTimer.current = setTimeout(
      () => goTo((curRef.current + 1) % MALABAS_SLIDES.length),
      MALABAS_DURATION
    );
    return () => { clearTimeout(slideTimer.current); clearTimeout(exitTimer.current); };
  }, [cur, goTo]);

  const slide = MALABAS_SLIDES[cur];

  return (
    <BrowserFrame url={slide.url} bg="#f0e8e0">
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {MALABAS_SLIDES.map((s, i) => {
          const isActive  = i === cur;
          const isExiting = i === exiting;
          let translateX = '100%';
          let opacity = 0;
          if (isActive)  { translateX = '0%'; opacity = 1; }
          if (isExiting) { translateX = '-6%'; opacity = 0; }
          return (
            <div key={i} style={{
              position: i === 0 ? 'relative' : 'absolute',
              top: 0, left: 0, width: '100%',
              transform: `translateX(${translateX})`,
              opacity,
              transition: isActive
                ? 'transform 0.45s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease'
                : 'transform 0.4s ease, opacity 0.3s ease',
              pointerEvents: isActive ? 'auto' : 'none',
            }}>
              <img src={s.img} alt={`Malabas ${s.url}`} style={{ width: '100%', display: 'block' }} />
            </div>
          );
        })}
      </div>
    </BrowserFrame>
  );
}

/* ── USC Campus Map ── */
export function USCMapPreview() {
  return (
    <BrowserFrame url="map.usc.ca" bg="#d4e8da">
      <img
        src={uscMapImg}
        alt="Western USC Campus Map"
        style={{ width: '100%', display: 'block' }}
      />
    </BrowserFrame>
  );
}

/* ── Silsila ── SVG phone frame + cycling screens ── */
const DURATION = 3500;
const SLIDES = [
  { img: silsilaScreen1, title: 'Find nearby institutes',   sub: 'Mosques, community centres & more' },
  { img: silsilaScreen2, title: 'BDS-safe businesses',      sub: 'Halal shops & services near you'   },
  { img: silsilaScreen1, title: 'Rich community profiles',  sub: 'Events, prayer times & contact info' },
];

export function SilsilaPreview() {
  const [cur, setCur]         = useState(0);
  const [exiting, setExiting] = useState(null);
  const exitTimer  = useRef(null);
  const slideTimer = useRef(null);
  const curRef     = useRef(0);

  const goTo = useCallback((n) => {
    const prev = curRef.current;
    setExiting(prev);
    setCur(n);
    curRef.current = n;
    clearTimeout(exitTimer.current);
    exitTimer.current = setTimeout(() => setExiting(null), 500);
  }, []);

  useEffect(() => {
    slideTimer.current = setTimeout(() => goTo((curRef.current + 1) % SLIDES.length), DURATION);
    return () => { clearTimeout(slideTimer.current); clearTimeout(exitTimer.current); };
  }, [cur, goTo]);

  const ACCENT = '#B94A37';

  return (
    <div style={{
      width: '100%', height: '100%', minHeight: 280,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', padding: '16px 0 14px',
    }}>
      <style>{KF}</style>

      {/* phone */}
      <div style={{
        position: 'relative', width: 210, flexShrink: 0,
        opacity: 0,
        animation: 'phoneIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.3s forwards, pp-float 6s ease-in-out 1.1s infinite',
      }}>
        {/* SVG frame — iPhone 15 Pro style */}
        <svg viewBox="0 0 320 650" style={{ width: '100%', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Titanium-like frame gradient */}
            <linearGradient id="sg-frameGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#4a4a4c"/>
              <stop offset="20%"  stopColor="#3a3a3c"/>
              <stop offset="50%"  stopColor="#1c1c1e"/>
              <stop offset="80%"  stopColor="#2c2c2e"/>
              <stop offset="100%" stopColor="#3a3a3c"/>
            </linearGradient>
            {/* Edge highlight — simulates the polished titanium band */}
            <linearGradient id="sg-edgeHighlight" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="rgba(255,255,255,0.18)"/>
              <stop offset="30%"  stopColor="rgba(255,255,255,0.06)"/>
              <stop offset="70%"  stopColor="rgba(255,255,255,0.10)"/>
              <stop offset="100%" stopColor="rgba(255,255,255,0.20)"/>
            </linearGradient>
            {/* Screen glass reflection */}
            <linearGradient id="sg-screenReflect" x1="0%" y1="0%" x2="12%" y2="100%">
              <stop offset="0%"   stopColor="rgba(255,255,255,0.055)"/>
              <stop offset="60%"  stopColor="rgba(255,255,255,0.01)"/>
              <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
            </linearGradient>
            {/* Drop shadow */}
            <filter id="sg-shadow" x="-15%" y="-10%" width="130%" height="130%">
              <feDropShadow dx="0" dy="10" stdDeviation="22" floodColor="rgba(0,0,0,0.75)"/>
              <feDropShadow dx="0" dy="2"  stdDeviation="4"  floodColor="rgba(0,0,0,0.4)"/>
            </filter>
            {/* Button gradient */}
            <linearGradient id="sg-btnGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#111"/>
              <stop offset="50%"  stopColor="#2a2a2a"/>
              <stop offset="100%" stopColor="#1a1a1a"/>
            </linearGradient>
          </defs>

          {/* Drop shadow base */}
          <rect x="8" y="12" width="304" height="634" rx="50" fill="rgba(0,0,0,0.45)" filter="url(#sg-shadow)"/>

          {/* Outer frame body */}
          <rect x="4" y="4" width="312" height="642" rx="50" fill="url(#sg-frameGrad)"/>

          {/* Polished edge highlight ring */}
          <rect x="4" y="4" width="312" height="642" rx="50" fill="none"
            stroke="url(#sg-edgeHighlight)" strokeWidth="1.5"/>

          {/* Inner bezel inset */}
          <rect x="12" y="12" width="296" height="626" rx="44" fill="none"
            stroke="rgba(0,0,0,0.6)" strokeWidth="1"/>

          {/* Screen black */}
          <rect x="18" y="20" width="284" height="610" rx="38" fill="#000"/>
          {/* Screen glass sheen */}
          <rect x="18" y="20" width="284" height="610" rx="38" fill="url(#sg-screenReflect)"/>

          {/* ── Left buttons ── */}
          {/* Mute/silent toggle */}
          <rect x="1" y="130" width="5" height="30" rx="2.5" fill="url(#sg-btnGrad)"/>
          <rect x="1" y="130" width="5" height="30" rx="2.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
          {/* Volume up */}
          <rect x="1" y="172" width="5" height="48" rx="2.5" fill="url(#sg-btnGrad)"/>
          <rect x="1" y="172" width="5" height="48" rx="2.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
          {/* Volume down */}
          <rect x="1" y="230" width="5" height="48" rx="2.5" fill="url(#sg-btnGrad)"/>
          <rect x="1" y="230" width="5" height="48" rx="2.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>

          {/* ── Right button — power ── */}
          <rect x="314" y="178" width="5" height="70" rx="2.5" fill="url(#sg-btnGrad)"/>
          <rect x="314" y="178" width="5" height="70" rx="2.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>

          {/* ── Dynamic Island ── */}
          <rect x="114" y="28" width="92" height="26" rx="13" fill="#050505"/>
          {/* FaceID camera dot */}
          <circle cx="186" cy="41" r="6"   fill="#0d0d0d"/>
          <circle cx="186" cy="41" r="3.5" fill="#050505"/>
          <circle cx="184" cy="39" r="1"   fill="rgba(255,255,255,0.04)"/>

          {/* ── Home indicator ── */}
          <rect x="115" y="620" width="90" height="4" rx="2" fill="rgba(255,255,255,0.18)"/>

          {/* ── Top edge specular ── */}
          <path d="M54 4.5 Q160 2.5 266 4.5" stroke="rgba(255,255,255,0.16)" strokeWidth="1" fill="none" strokeLinecap="round"/>
        </svg>

        {/* Screen content — positioned over SVG */}
        <div style={{
          position: 'absolute',
          top: '3.1%', left: '5.7%', right: '5.7%', bottom: '3.1%',
          borderRadius: 38, overflow: 'hidden', background: '#000',
        }}>
          {SLIDES.map((slide, i) => {
            const isActive  = i === cur;
            const isExiting = i === exiting;
            let opacity   = 0;
            let transform = 'scale(0.97) translateY(8px)';
            let transition = 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)';
            if (isActive)  { opacity = 1; transform = 'scale(1) translateY(0)'; }
            if (isExiting) { opacity = 0; transform = 'scale(0.97) translateY(-8px)'; transition = 'opacity 0.4s ease, transform 0.4s ease'; }
            return (
              <div key={i} style={{ position: 'absolute', inset: 0, opacity, transform, transition }}>
                <img
                  src={slide.img}
                  alt={slide.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                />
                {/* Dot indicators — inside the screen at the bottom */}
                <div style={{
                  position: 'absolute', bottom: 14, left: 0, right: 0,
                  display: 'flex', justifyContent: 'center', gap: 5,
                  pointerEvents: 'none',
                }}>
                  {SLIDES.map((_, di) => (
                    <div key={di} style={{
                      width: cur === di ? 18 : 5,
                      height: 5, borderRadius: cur === di ? 2.5 : '50%',
                      background: cur === di ? '#fff' : 'rgba(255,255,255,0.4)',
                      transition: 'all 0.35s ease',
                    }} />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Progress bar — key=cur restarts each slide */}
          <div key={cur} style={{
            position: 'absolute', bottom: 0, left: 0,
            height: 2, background: ACCENT,
            animation: `progRun ${DURATION}ms linear forwards`,
            opacity: 0.7,
          }} />
        </div>
      </div>
    </div>
  );
}

/* ── Studyscape ── */
export function StudyscapePreview() {
  return (
    <div style={{
      width: '100%', height: '100%', minHeight: 280,
      background: '#e8e4d4', borderRadius: 12,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', padding: '12px',
    }}>
      <style>{KF}</style>
      <img
        src={studyscapeImg}
        alt="Studyscape productivity hub"
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          animation: 'pp-float 7s ease-in-out 0.5s infinite',
          display: 'block',
        }}
      />
    </div>
  );
}

/* ── Router ── */
export function ProjectPreview({ slug, id, color }) {
  if (slug === 'malabas')     return <MalabasPreview />;
  if (slug === 'silsila')     return <SilsilaPreview />;
  if (id === 4 || id === '4') return <USCMapPreview />;
  if (id === 2 || id === '2') return <StudyscapePreview />;
  return (
    <div style={{
      width: '100%', height: '100%', minHeight: 280,
      background: color || '#e8e4d4', borderRadius: 12,
    }} />
  );
}
